import { singleton, inject } from "tsyringe";

// shared
import type { HttpAdapter } from "@/shared/libs/http/interface";

// domain
import {
    CurriculumMetadataRepository,
    RequestGetUserCurriculumMetadatasDto,
    RequestPatchCurriculumMetadataDto,
    RequestPostCurriculumMetadatasDto,
} from "@/entities/curriculum-metadata/domain/repository";
import type {
    GetCurriculumMetadataResponseDto,
    GetUserCurriculumMetadatasResponseDto,
    CurriculumMetadataDto,
} from "@/entities/curriculum-metadata/domain/dtos";

@singleton()
export default class CurriculumMetadataRepositoryImpl implements CurriculumMetadataRepository {
    constructor(@inject("HttpAdapter") private readonly server: HttpAdapter) {}

    /**
     * @description 특정 커리큘럼에 대한 상세 정보를 호출합니다.
     */
    public getCurriculumMetadata = async (curriculumMetadataId: string) => {
        const response = await this.server.get<GetCurriculumMetadataResponseDto>(
            `/v1/curriculum-metadatas/${curriculumMetadataId}`,
        );

        return response;
    };

    /**
     * @description 유저의 커리큘럼 리스트를 불러옵니다.
     */
    public getUserCurriculumMetadatas = async (payload: RequestGetUserCurriculumMetadatasDto) => {
        let url = `/v1/curriculum-metadatas?userId=${payload.studentId}&status=${payload.status}&`;

        if (payload.size) {
            url += `size=${payload.size}&`;
        } else {
            url += `size=10&`;
        }

        if (payload.nextToken) {
            url += `nextToken=${payload.nextToken}&`;
        }

        const response = await this.server.get<GetUserCurriculumMetadatasResponseDto>(url);

        return response;
    };

    /** @description 커리큘럼을 생성합니다. */
    public postCurriculumMetadata = async (payload: RequestPostCurriculumMetadatasDto) => {
        const response = await this.server.post<CurriculumMetadataDto>(
            `/v1/curriculum-metadatas`,
            payload.requestDto,
        );

        return response;
    };

    /** @description 커리큘럼을 변경합니다. 여기서의 변경은 커리큘럼의 이름이나 순서입니다. */
    public patchCurriculumMetadata = async (payload: RequestPatchCurriculumMetadataDto) => {
        const response = await this.server.patch<CurriculumMetadataDto>(
            `/v1/curriculum-metadatas/${payload.curriculumMetadataId}`,
            payload.requestDto,
        );

        return response;
    };

    /** @description 커리큘럼을 삭제합니다. */
    public deleteCurriculumMetadata = async (curriculumMetadataId: string) => {
        await this.server.delete(`/v1/curriculum-metadatas/${curriculumMetadataId}`);
    };
}
